import React from "react";
import {
    Popper,
    Autocomplete,
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    DeleteOutline,
    DragIndicator,
    ExpandLess,
    ExpandMore,
    InfoOutlined,
} from "@mui/icons-material";
import { DragDropContext, Draggable, type DropResult } from "react-beautiful-dnd";
import { StrictModeDroppable } from "../../../molecules/StrictModeDroppable";
import {
    type CompoundCondition,
    type ConditionClause,
    type Question,
    type WorkflowQuestion,
    InclusionMode,
    WorkflowTriggerAction,
} from "../../../../types/rubric";
import type { AnswerValue } from "../../../../types/answerSet";
import ExpressionBuilder from "./ExpressionBuilder";
import { supportsCrossWorkflowTargets } from "./workflowTriggerCapabilities";

type WorkflowQuestionWithClientId = WorkflowQuestion & {
    clientId: string;
}

type ConditionQuestionOption = {
    questionId: number;
    label: string;
    disabled?: boolean;
}

const operatorOptions = [{ value: "IS", label: "is" }];
const actionOptions = [
    { value: "GO_TO", label: "Go to question" },
    { value: "STOP", label: "Stop workflow" },
    { value: "SET_ANSWERS", label: "Set answers" },
    { value: "AUTOFILL", label: "Autofill answer" },
];

// SPAN/SPANS questions only support STOP and GO_TO actions (no answer set to filter/autofill)
const spanActionOptions = [
    { value: "GO_TO", label: "Go to question" },
    { value: "STOP", label: "Stop workflow" },
];

// Sentinel answer IDs for SPAN/SPANS condition rules
const SPAN_ANSWERED_ID = -3;
const SPAN_NOT_ANSWERED_ID = -4;

const spanConditionOptions = [
    { value: SPAN_ANSWERED_ID, label: "Span is selected" },
    { value: SPAN_NOT_ANSWERED_ID, label: "No span selected" },
];

const isSpanQuestionType = (questionType: string): boolean =>
    questionType === "SPAN" || questionType === "SPANS";

// Action configuration for dynamic input rendering
const actionConfig: Record<string, {
    hasInput: boolean;
    inputType?: string;
    inputLabel?: string;
    placeholder?: string;
    needsToAnswerValueIds?: boolean;
}> = {
    GO_TO: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question'
    },
    STOP: {
        hasInput: false
    },
    SET_ANSWERS: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question',
        needsToAnswerValueIds: true
    },
    AUTOFILL: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question',
        needsToAnswerValueIds: true
    }
};

const questionCardPalette = {
    background: "#F7FDFF",
    header: "#E9F3FF",
    border: "#BBDCFB",
    triggerBackground: "#F7FDFF",
};

export interface QuestionsSectionProps {
    questions: WorkflowQuestionWithClientId[];
    validTargetQuestions?: WorkflowQuestionWithClientId[];
    allQuestions?: WorkflowQuestionWithClientId[];
    onDragEnd: (result: DropResult) => void;
    collapsedSections: Record<string, boolean>;
    onToggleSection: (questionKey: string) => void;
    onRemoveQuestion: (clientId: string) => void;
    onAddTrigger: (clientId: string) => void;
    onRemoveTrigger: (clientId: string, triggerIndex: number) => void;
    onTriggerAnswersChange: (clientId: string, triggerIndex: number, values: AnswerValue[]) => void;
    onTriggerTargetChange: (clientId: string, triggerIndex: number, targetId: number | undefined) => void;
    onTriggerActionChange: (clientId: string, triggerIndex: number, action: WorkflowTriggerAction) => void;
    onTriggerToAnswersChange?: (clientId: string, triggerIndex: number, answerIds: number[]) => void;
    onConditionClauseChange?: (clientId: string, triggerIndex: number, clauseIndex: number, field: keyof ConditionClause, value: number | number[]) => void;
    onAddConditionClause?: (clientId: string, triggerIndex: number) => void;
    onRemoveConditionClause?: (clientId: string, triggerIndex: number, clauseIndex: number) => void;
    onOperatorChange?: (clientId: string, triggerIndex: number, operator: CompoundCondition["operator"]) => void;
    questionsAvailableToAdd?: Question[];
    onAddQuestion: (questionId: number) => void;
    showAddQuestion?: boolean;
    droppableId: string;
    onTriggerDragEnd?: (questionClientId: string, sourceIndex: number, destinationIndex: number) => void;
    onExpressionChange?: (clientId: string, expression: string) => void;
    /**
     * Full pool of system + workflow questions known to the workflow editor
     * (i.e. props.questions in CodeWorkflowsPanel). Used to resolve a
     * trigger reference to a question that's been removed from the
     * workflow but still exists in the system, so we can show its name
     * with a "removed from workflow" suffix instead of leaking the id.
     */
    globalQuestionPool?: Question[];
    /**
     * Cross-workflow target questions to surface in the trigger Target
     * dropdown for actions that support cross-workflow targets. Today that
     * is only AUTOFILL, but the action capability is centralized so future
     * trigger types can opt in without changing the picker plumbing.
     *
     * Each entry's `question.name` should already be prefixed with its
     * source workflow name so the dropdown disambiguates (e.g. "Procedure
     * Workflow: Was X done?"). Empty/undefined keeps the existing host-only
     * behaviour.
     *
     * SPAN workflows must be excluded by the parent before passing through
     * to this prop. Unsupported actions remain host-only regardless of this
     * prop.
     */
    crossWorkflowTargetQuestions?: WorkflowQuestionWithClientId[];
}

const getActiveAnswers = (question: Question) => {
    if (!question || !question.answers) {
        return [];
    }
    return question.answers?.filter((answer) => answer.active).filter((answer) => {
        if (question.inclusionMode === InclusionMode.INCLUDE) {
            return question.selectedAnswerIds?.includes(answer.id);
        } else {
            return !question.selectedAnswerIds?.includes(answer.id);
        }
    });
}

const formatConditionQuestionLabel = (
    questionName: string,
    questionType?: string,
    suffix?: string,
) => `${questionName}${questionType === "SYSTEM" ? " (System)" : ""}${suffix ? ` (${suffix})` : ""}`;

const buildConditionQuestionOptions = (
    allQuestions: WorkflowQuestionWithClientId[],
    hostQuestionId: number,
    selectedQuestionId?: number,
    globalQuestionPool: Question[] = [],
): ConditionQuestionOption[] => {
    const hostIndex = allQuestions.findIndex((question) => question.question.id === hostQuestionId);
    const baseOptions = hostIndex === -1 ? allQuestions : allQuestions.slice(0, hostIndex + 1);
    if (selectedQuestionId == null || baseOptions.some((question) => question.question.id === selectedQuestionId)) {
        return baseOptions.map((workflowQuestion) => ({
            questionId: workflowQuestion.question.id,
            label: formatConditionQuestionLabel(workflowQuestion.question.name, workflowQuestion.question.questionType),
        }));
    }

    const selectedQuestion = allQuestions.find((question) => question.question.id === selectedQuestionId);
    if (!selectedQuestion) {
        // Fall back to the global question pool so a question that's been
        // removed from the workflow still shows its NAME (with a "removed
        // from workflow" suffix) instead of leaking the numeric id. Only
        // shows the bare id if the question is gone from the system entirely.
        const removedQuestion = globalQuestionPool.find((q) => q.id === selectedQuestionId);
        return [
            ...baseOptions.map((workflowQuestion) => ({
                questionId: workflowQuestion.question.id,
                label: formatConditionQuestionLabel(workflowQuestion.question.name, workflowQuestion.question.questionType),
            })),
            {
                questionId: selectedQuestionId,
                label: removedQuestion
                    ? formatConditionQuestionLabel(removedQuestion.name, removedQuestion.questionType, "removed from workflow")
                    : `Question #${selectedQuestionId} (removed from workflow)`,
                disabled: true,
            },
        ];
    }

    return [
        ...baseOptions.map((workflowQuestion) => ({
            questionId: workflowQuestion.question.id,
            label: formatConditionQuestionLabel(workflowQuestion.question.name, workflowQuestion.question.questionType),
        })),
        {
            questionId: selectedQuestion.question.id,
            label: formatConditionQuestionLabel(selectedQuestion.question.name, selectedQuestion.question.questionType, "later question"),
            disabled: true,
        },
    ];
}

/**
 * Small wrapper around MUI Autocomplete that owns its own inputValue
 * state so we can force the input back to empty after each selection.
 *
 * Why a sub-component: the parent Autocomplete's `clearOnBlur` only
 * clears when the typed value doesn't match a valid option — but after
 * a selection the input value DOES match the selected option's label,
 * so `clearOnBlur` is a no-op. Controlled `inputValue` lets us reset
 * explicitly in `onChange` (for the user-selection case) and treat
 * MUI's reason='reset' input-change event (fired by the library on
 * select) as "force empty" rather than "set to selected label".
 */
const AddQuestionAutocomplete: React.FC<{
    options: Question[];
    onAddQuestion: (questionId: number) => void;
}> = ({ options, onAddQuestion }) => {
    // We force-remount the Autocomplete after each successful add by bumping
    // the `addCounter` and using it as a React key. This is more reliable than
    // controlling `inputValue` and trying to wrestle MUI's internal reset
    // handling — under real mouse-click sequencing the controlled value can
    // be re-asserted to the just-selected option's label by MUI's internal
    // reconciliation, leaving the input visibly populated until blur. Remount
    // discards all of that state in a single render.
    const [addCounter, setAddCounter] = React.useState(0);
    return (
        <Autocomplete<Question, false, false, false>
            key={`add-question-${addCounter}`}
            fullWidth
            blurOnSelect
            // Render the popper at the anchor's CURRENT measured client
            // width and force the inner Paper + listbox to fill that
            // popper. Without the inner overrides, the listbox inside the
            // popper renders at the longest option's content width and
            // leaves visible empty space on the right of the dropdown.
            // 100% on the inner slots is now safe because the popper has
            // an explicit pixel width (no body-portal sizing).
            PopperComponent={(popperProps) => {
                const anchorWidth =
                    popperProps.anchorEl instanceof HTMLElement
                        ? popperProps.anchorEl.clientWidth
                        : undefined;
                return (
                    <Popper
                        {...popperProps}
                        style={{
                            ...popperProps.style,
                            width: anchorWidth ? `${anchorWidth}px` : popperProps.style?.width,
                        }}
                    />
                );
            }}
            slotProps={{
                paper: { sx: { width: "100%" } },
            }}
            // ListboxProps is the right knob for the inner <ul>; the
            // `listbox` key under slotProps is silently ignored by MUI v5's
            // Autocomplete (its supported slots are popper / paper /
            // popupIndicator / clearIndicator / root only). Without this,
            // the listbox sized itself to the longest option's text width
            // and rendered visibly narrower than the popper Paper.
            ListboxProps={{
                style: { width: "100%", boxSizing: "border-box" },
            }}
            options={options}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={null}
            onChange={(_, newValue) => {
                if (newValue) {
                    onAddQuestion(newValue.id);
                    setAddCounter((n) => n + 1);
                }
            }}
            renderInput={(params) => (
                <TextField {...params} label="Add question" />
            )}
        />
    );
};

const QuestionsSection: React.FC<QuestionsSectionProps> = ({
    questions,
    validTargetQuestions,
    allQuestions = [],
    onDragEnd,
    collapsedSections,
    onToggleSection,
    onRemoveQuestion,
    onAddTrigger,
    onRemoveTrigger,
    onTriggerAnswersChange,
    onTriggerTargetChange,
    onTriggerActionChange,
    onTriggerToAnswersChange,
    onConditionClauseChange,
    onAddConditionClause,
    onRemoveConditionClause,
    onOperatorChange,
    questionsAvailableToAdd = [],
    onAddQuestion,
    showAddQuestion = true,
    droppableId,
    onTriggerDragEnd,
    onExpressionChange,
    globalQuestionPool = [],
    crossWorkflowTargetQuestions = [],
}) => {

    // Get answers for any question by ID (used for compound condition clauses)
    const getAnswersForQuestion = (questionId: number): AnswerValue[] => {
        const wq = allQuestions.find((q) => q.question.id === questionId);
        if (!wq) return [];
        if (wq.question.questionType === "SYSTEM") {
            return [
                { id: -1, name: "true", displayText: "Expression is true", active: true } as AnswerValue,
                { id: -2, name: "false", displayText: "Expression is false", active: true } as AnswerValue,
            ];
        }
        if (isSpanQuestionType(wq.question.questionType)) {
            return [
                { id: SPAN_ANSWERED_ID, name: "span_answered", displayText: "Span is selected", active: true } as AnswerValue,
                { id: SPAN_NOT_ANSWERED_ID, name: "span_not_answered", displayText: "No span selected", active: true } as AnswerValue,
            ];
        }
        return getActiveAnswers(wq.question);
    };

    const renderTriggerSection = (workflowQuestion: WorkflowQuestionWithClientId) => {
        const question = workflowQuestion.question;
        const clientId = workflowQuestion.clientId;

        // Triggers are not available for FREE_TEXT questions since rules cannot be evaluated on free text responses
        if (question.questionType === "FREE_TEXT") {
            return (
                <Stack spacing={1.5} sx={{ width: "100%", flexDirection: "column", backgroundColor: questionCardPalette.background }}>
                    <Typography variant="subtitle2">Triggers</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Triggers are not available for free text questions. Rules cannot be evaluated on free text responses.
                    </Typography>
                </Stack>
            );
        }

        const isSpan = isSpanQuestionType(question.questionType);

        // SYSTEM questions use synthetic true/false answers based on expression result
        // SPAN/SPANS questions use sentinel IDs for condition rules (handled separately in the UI)
        const activeAnswers: AnswerValue[] = question.questionType === 'SYSTEM'
            ? [
                { id: -1, name: 'true', displayText: 'Expression is true', active: true } as AnswerValue,
                { id: -2, name: 'false', displayText: 'Expression is false', active: true } as AnswerValue,
            ]
            : isSpan
            ? [
                { id: SPAN_ANSWERED_ID, name: 'span_answered', displayText: 'Span is selected', active: true } as AnswerValue,
                { id: SPAN_NOT_ANSWERED_ID, name: 'span_not_answered', displayText: 'No span selected', active: true } as AnswerValue,
            ]
            : getActiveAnswers(question);
        // Host-workflow target options (used for STOP / GO_TO / SET_ANSWERS
        // triggers and as the base set for AUTOFILL).
        // SystemQuestions can target both SystemQuestions and WorkflowQuestions;
        // WorkflowQuestions can only target other WorkflowQuestions (the parent
        // controls scope by the `validTargetQuestions` it passes in).
        const hostTargetOptions: { id: number; name: string }[] = (validTargetQuestions ?? [])
            .filter((q) => q.question.id !== question.id)
            .map((q) => ({
                id: q.question.id,
                name: q.question.name,
            }));            

        // Cross-workflow target options (PROCEDURE/DIAGNOSIS/MODIFIER questions
        // from sibling workflows in the rubric) only surface for actions that
        // support cross-workflow targets when the parent passed them in
        // (case-host only). Currently AUTOFILL is the only supported action.
        // SPAN workflow exclusion and host-CASE gating happen in the parent;
        // additionally filter SPAN/SPANS *questions* here, since the backend
        // rejects SPAN-typed questions as cross-workflow targets
        // (no answer-set to fill).
        const crossWorkflowTargetOptions: { id: number; name: string }[] = (
            crossWorkflowTargetQuestions ?? []
        )
            .filter((q) => q.question.id !== question.id)
            .filter((q) => !isSpanQuestionType(q.question.questionType))
            .map((q) => ({
                id: q.question.id,
                name: q.question.name,
            }));

        const computeTargetOptionsForAction = (
            action: WorkflowTriggerAction | undefined,
        ): { id: number; name: string }[] => {
            if (supportsCrossWorkflowTargets(action)) {
                // Concatenate host then cross-workflow so within-workflow
                // targets remain available alongside cross-workflow ones.
                const seen = new Set<number>();
                const merged: { id: number; name: string }[] = [];
                for (const opt of [...hostTargetOptions, ...crossWorkflowTargetOptions]) {
                    if (!seen.has(opt.id)) {
                        seen.add(opt.id);
                        merged.push(opt);
                    }
                }
                return merged;
            }
            return hostTargetOptions;
        };


        return (
            <Stack spacing={1.5} sx={{ width: "100%", flexDirection: "column", backgroundColor: questionCardPalette.background }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ md: "center" }}
                >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="subtitle2">Triggers</Typography>
                        <Tooltip title="Triggers execute top to bottom. When multiple triggers match the same answer, all execute. For conflicting actions, the last trigger takes priority.">
                            <InfoOutlined fontSize="small" sx={{ color: "text.secondary", fontSize: 16 }} />
                        </Tooltip>
                    </Stack>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => onAddTrigger(clientId)}
                        disabled={activeAnswers.length === 0}
                    >
                        Add Trigger
                    </Button>
                </Stack>

                {(workflowQuestion.triggers ?? []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No triggers yet. Add one to define the workflow logic.
                    </Typography>
                ) : (
                    <StrictModeDroppable droppableId={`triggers-${clientId}`} type="TRIGGER">
                        {(triggerDroppableProvided) => (
                            <Stack spacing={1.5} ref={triggerDroppableProvided.innerRef} {...triggerDroppableProvided.droppableProps}>
                                {(workflowQuestion.triggers ?? []).map((trigger, triggerIndex) => (
                                    <Draggable
                                        key={`trigger-${clientId}-${trigger._clientId ?? trigger.id ?? triggerIndex}`}
                                        draggableId={`trigger-${clientId}-${trigger._clientId ?? trigger.id ?? triggerIndex}`}
                                        index={triggerIndex}
                                    >
                                        {(triggerDraggableProvided) => (
                                            <Box
                                                ref={triggerDraggableProvided.innerRef}
                                                {...triggerDraggableProvided.draggableProps}
                                                sx={{
                                                    border: `1px solid ${questionCardPalette.border}`,
                                                    backgroundColor: questionCardPalette.triggerBackground,
                                                    borderRadius: "3px",
                                                    p: 2,
                                                }}
                                            >
                                                <Stack spacing={2}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Box
                                                            component="span"
                                                            {...triggerDraggableProvided.dragHandleProps}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                cursor: "grab",
                                                                color: "text.secondary",
                                                            }}
                                                        >
                                                            <DragIndicator fontSize="small" />
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            #{triggerIndex + 1}
                                                        </Typography>
                                                    </Stack>
                                                    {/* Compound condition clauses */}
                                                    {(trigger.compoundCondition?.conditions ?? [{ questionId: question.id, answerValueIds: trigger.answerValueIds ?? [] }]).map((clause, clauseIndex) => {
                                                        const clauseAnswers = getAnswersForQuestion(clause.questionId);
                                                        const clauseQuestionOptions = buildConditionQuestionOptions(
                                                            allQuestions,
                                                            question.id,
                                                            clause.questionId,
                                                            globalQuestionPool,
                                                        );

                                                        return (
                                                            <React.Fragment key={`clause-${clauseIndex}`}>
                                                                {clauseIndex > 0 && (
                                                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                                                        <FormControl size="small" sx={{ minWidth: 80 }}>
                                                                            <Select
                                                                                value={trigger.compoundCondition?.operator ?? "AND"}
                                                                                onChange={(event) => {
                                                                                    if (onOperatorChange) {
                                                                                        onOperatorChange(clientId, triggerIndex, event.target.value as "AND" | "OR");
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <MenuItem value="AND">AND</MenuItem>
                                                                                <MenuItem value="OR">OR</MenuItem>
                                                                            </Select>
                                                                        </FormControl>
                                                                    </Stack>
                                                                )}
                                                                <Stack
                                                                    direction={{ xs: "column", md: "row" }}
                                                                    spacing={1}
                                                                    alignItems={{ md: "center" }}
                                                                >
                                                                    <Typography variant="subtitle2">If</Typography>

                                                                    <Autocomplete<ConditionQuestionOption, false, false, false>
                                                                        sx={{ flex: 1, minWidth: 0 }}
                                                                        size="small"
                                                                        fullWidth
                                                                        options={clauseQuestionOptions}
                                                                        getOptionLabel={(option) => option.label}
                                                                        getOptionDisabled={(option) => !!option.disabled}
                                                                        isOptionEqualToValue={(option, value) => option.questionId === value.questionId}
                                                                        value={clauseQuestionOptions.find((opt) => opt.questionId === (clause.questionId ?? question.id)) ?? null}
                                                                        onChange={(_, newValue) => {
                                                                            if (newValue && onConditionClauseChange) {
                                                                                onConditionClauseChange(clientId, triggerIndex, clauseIndex, "questionId", newValue.questionId);
                                                                            }
                                                                        }}
                                                                        renderInput={(params) => (
                                                                            <TextField {...params} label="Question" size="small" />
                                                                        )}
                                                                    />

                                                                    <FormControl sx={{ minWidth: 60 }} size="small">
                                                                        <InputLabel id={`operator-${clientId}-${triggerIndex}-${clauseIndex}`}>Operator</InputLabel>
                                                                        <Select
                                                                            labelId={`operator-${clientId}-${triggerIndex}-${clauseIndex}`}
                                                                            label="Operator"
                                                                            value={operatorOptions[0].value}
                                                                            disabled
                                                                        >
                                                                            {operatorOptions.map((option) => (
                                                                                <MenuItem key={option.value} value={option.value}>
                                                                                    {option.label}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    </FormControl>

                                                                    {(() => {
                                                                        const clauseWq = (allQuestions ?? []).find((q) => q.question.id === clause.questionId);
                                                                        const clauseIsSpan = clauseWq ? isSpanQuestionType(clauseWq.question.questionType) : isSpanQuestionType(question.questionType);
                                                                        const clauseQuestionRequired = clauseWq?.question.required ?? question.required ?? false;

                                                                        if (clauseIsSpan) {
                                                                            return (
                                                                                <FormControl sx={{ flex: 1, minWidth: 0 }} size="small" fullWidth>
                                                                                    <InputLabel id={`span-condition-${clientId}-${triggerIndex}-${clauseIndex}`}>Condition</InputLabel>
                                                                                    <Select
                                                                                        labelId={`span-condition-${clientId}-${triggerIndex}-${clauseIndex}`}
                                                                                        label="Condition"
                                                                                        value={clause.answerValueIds?.[0] ?? ""}
                                                                                        onChange={(event) => {
                                                                                            const selectedId = Number(event.target.value);
                                                                                            if (onConditionClauseChange) {
                                                                                                onConditionClauseChange(
                                                                                                    clientId,
                                                                                                    triggerIndex,
                                                                                                    clauseIndex,
                                                                                                    "answerValueIds",
                                                                                                    selectedId ? [selectedId] : [],
                                                                                                );
                                                                                            } else {
                                                                                                const syntheticAnswer = clauseAnswers.find((a) => a.id === selectedId);
                                                                                                onTriggerAnswersChange(
                                                                                                    clientId,
                                                                                                    triggerIndex,
                                                                                                    syntheticAnswer ? [syntheticAnswer] : [],
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        {spanConditionOptions
                                                                                            .filter((option) => !(option.value === SPAN_NOT_ANSWERED_ID && clauseQuestionRequired))
                                                                                            .map((option) => (
                                                                                            <MenuItem key={option.value} value={option.value}>
                                                                                                {option.value === SPAN_NOT_ANSWERED_ID ? (
                                                                                                    <Tooltip title="If no span is selected, the next question in sequence will be shown automatically" placement="right">
                                                                                                        <span>{option.label}</span>
                                                                                                    </Tooltip>
                                                                                                ) : (
                                                                                                    option.label
                                                                                                )}
                                                                                            </MenuItem>
                                                                                        ))}
                                                                                    </Select>
                                                                                </FormControl>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <Autocomplete<AnswerValue, true, false, false>
                                                                                multiple
                                                                                disableCloseOnSelect
                                                                                options={clauseAnswers}
                                                                                getOptionLabel={(option) => option.name ?? option.displayText ?? ""}
                                                                                value={clauseAnswers.filter((answer) => clause.answerValueIds?.includes(answer.id))}
                                                                                onChange={(_, values: AnswerValue[]) => {
                                                                                    if (onConditionClauseChange) {
                                                                                        onConditionClauseChange(
                                                                                            clientId,
                                                                                            triggerIndex,
                                                                                            clauseIndex,
                                                                                            "answerValueIds",
                                                                                            values.map((v) => v.id as number),
                                                                                        );
                                                                                    } else {
                                                                                        // Fallback for legacy single-condition mode
                                                                                        onTriggerAnswersChange(clientId, triggerIndex, values);
                                                                                    }
                                                                                }}
                                                                                renderOption={(props, option, { selected }) => {
                                                                                    const { key, ...restProps } = props;
                                                                                    const selectedAnswers = clauseAnswers.filter((answer) => clause.answerValueIds?.includes(answer.id));
                                                                                    const allSelected = selectedAnswers.length === clauseAnswers.length;
                                                                                    const isFirstOption = clauseAnswers.indexOf(option) === 0;

                                                                                    return (
                                                                                        <React.Fragment key={key}>
                                                                                            {isFirstOption && clauseAnswers.length > 1 && (
                                                                                                <Box
                                                                                                    component="li"
                                                                                                    sx={{
                                                                                                        fontWeight: 600,
                                                                                                        borderBottom: "1px solid #e0e0e0",
                                                                                                        py: 1,
                                                                                                        px: 2,
                                                                                                        cursor: "pointer",
                                                                                                        "&:hover": { backgroundColor: "action.hover" },
                                                                                                    }}
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        if (onConditionClauseChange) {
                                                                                                            onConditionClauseChange(
                                                                                                                clientId,
                                                                                                                triggerIndex,
                                                                                                                clauseIndex,
                                                                                                                "answerValueIds",
                                                                                                                allSelected ? [] : clauseAnswers.map((a) => a.id as number),
                                                                                                            );
                                                                                                        }
                                                                                                    }}
                                                                                                >
                                                                                                    {allSelected ? "Deselect All" : "Select All"}
                                                                                                </Box>
                                                                                            )}
                                                                                            <Box component="li" {...restProps}>
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={selected}
                                                                                                    readOnly
                                                                                                    style={{ marginRight: 8 }}
                                                                                                />
                                                                                                {option.name ?? option.displayText ?? ""}
                                                                                            </Box>
                                                                                        </React.Fragment>
                                                                                    );
                                                                                }}
                                                                                renderInput={(params) => (
                                                                                    <TextField {...params} label="Answers" placeholder="Select answers" size="small" />
                                                                                )}
                                                                                fullWidth
                                                                                sx={{ flex: 1, minWidth: 0 }}
                                                                            />
                                                                        );
                                                                    })()}
                                                                    {trigger.compoundCondition && trigger.compoundCondition.conditions.length > 1 && (
                                                                        <Tooltip title="Remove condition">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="secondary"
                                                                                onClick={() => onRemoveConditionClause?.(clientId, triggerIndex, clauseIndex)}
                                                                            >
                                                                                <DeleteOutline fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Stack>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                    {trigger.compoundCondition && (
                                                        <Button
                                                            variant="text"
                                                            size="small"
                                                            startIcon={<Add />}
                                                            onClick={() => onAddConditionClause?.(clientId, triggerIndex)}
                                                            sx={{ alignSelf: "flex-start" }}
                                                        >
                                                            Add Condition
                                                        </Button>
                                                    )}
                                                    <Stack
                                                        direction={{ xs: "column", md: "row" }}
                                                        spacing={1}
                                                        alignItems={{ md: "center" }}
                                                    >
                                                        <Typography variant="subtitle2">Then</Typography>
                                                        <FormControl sx={{ minWidth: 160 }} size="small">
                                                            <InputLabel id={`action-${clientId}-${triggerIndex}`}>Action</InputLabel>
                                                            <Select
                                                                labelId={`action-${clientId}-${triggerIndex}`}
                                                                label="Action"
                                                                value={trigger.action ?? "GO_TO"}
                                                                onChange={(event) =>
                                                                    onTriggerActionChange(
                                                                        clientId,
                                                                        triggerIndex,
                                                                        event.target.value as WorkflowTriggerAction,
                                                                    )
                                                                }
                                                            >
                                                                {(isSpan ? spanActionOptions : actionOptions).map((option) => (
                                                                    <MenuItem key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        {actionConfig[trigger.action || 'GO_TO']?.hasInput && (() => {
                                                            const targetLabel = actionConfig[trigger.action || 'GO_TO']?.inputLabel || 'Action Parameter';
                                                            const targetPlaceholder = actionConfig[trigger.action || 'GO_TO']?.placeholder || 'Add another question to target';
                                                            const triggerTargetOptions = computeTargetOptionsForAction(trigger.action);
                                                            const selectedTarget = triggerTargetOptions.find((opt) => opt.id === trigger.toQuestionId) ?? null;
                                                            return (
                                                                <Autocomplete<{ id: number; name: string }, false, false, false>
                                                                    fullWidth
                                                                    size="small"
                                                                    options={triggerTargetOptions}
                                                                    getOptionLabel={(option) => option.name}
                                                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                    value={selectedTarget}
                                                                    noOptionsText={targetPlaceholder}
                                                                    onChange={(_, newValue) => {
                                                                        // Pass through `undefined` when the user clears the
                                                                        // selection so the parent can wipe a stale toQuestionId
                                                                        // from trigger state.
                                                                        onTriggerTargetChange(
                                                                            clientId,
                                                                            triggerIndex,
                                                                            newValue?.id,
                                                                        );
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params} label={targetLabel} size="small" />
                                                                    )}
                                                                />
                                                            );
                                                        })()}
                                                        {actionConfig[trigger.action || 'GO_TO']?.needsToAnswerValueIds && (trigger.toQuestionId) && (() => {
                                                            // Find the target question to get its answers, including
                                                            // cross-workflow questions only for actions that support
                                                            // cross-workflow targets.
                                                            const targetSearchPool: WorkflowQuestionWithClientId[] = [
                                                                ...(validTargetQuestions ?? questions),
                                                                ...(supportsCrossWorkflowTargets(trigger.action)
                                                                    ? (crossWorkflowTargetQuestions ?? [])
                                                                    : []),
                                                            ];
                                                            const targetQuestion = targetSearchPool.find(
                                                                (q) => q.question.id === trigger.toQuestionId
                                                            );
                                                            const targetAnswers = getActiveAnswers(targetQuestion?.question);

                                                            if (targetAnswers.length === 0) {
                                                                return null;
                                                            }

                                                            const isAutofill = trigger.action === "AUTOFILL";

                                                            return (
                                                                <FormControl fullWidth size="small">
                                                                    <InputLabel id={`trigger-to-answers-${clientId}-${triggerIndex}`}>
                                                                        {isAutofill ? "Answer to Autofill" : "Answers to Set"}
                                                                    </InputLabel>
                                                                    {isAutofill ? (
                                                                        <Select
                                                                            labelId={`trigger-to-answers-${clientId}-${triggerIndex}`}
                                                                            placeholder={isAutofill ? "Answer to Autofill" : "Answers to Set"}
                                                                            value={trigger.toAnswerValueIds?.[0] ?? ""}
                                                                            onChange={(event) => {
                                                                                if (onTriggerToAnswersChange) {
                                                                                    const answerId = event.target.value ? Number(event.target.value) : undefined;
                                                                                    onTriggerToAnswersChange(
                                                                                        clientId,
                                                                                        triggerIndex,
                                                                                        answerId !== undefined ? [answerId] : [],
                                                                                    );
                                                                                }
                                                                            }}
                                                                        >
                                                                            {targetAnswers.map((answer) => (
                                                                                <MenuItem key={answer.id} value={answer.id}>
                                                                                    {answer.name ?? answer.displayText ?? ""}
                                                                                </MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                    ) : (
                                                                        <Autocomplete<AnswerValue, true, false, false>
                                                                            multiple
                                                                            options={targetAnswers}
                                                                            getOptionLabel={(option) => option.name ?? option.displayText ?? ""}
                                                                            value={targetAnswers.filter((answer) => trigger.toAnswerValueIds?.includes(answer.id))}
                                                                            onChange={(_, values: AnswerValue[]) => {
                                                                                if (onTriggerToAnswersChange) {
                                                                                    onTriggerToAnswersChange(
                                                                                        clientId,
                                                                                        triggerIndex,
                                                                                        values.map((v) => v.id as number),
                                                                                    );
                                                                                }
                                                                            }}
                                                                            renderInput={(params) => (
                                                                                <TextField {...params} label="Answers to Set" placeholder="Select answers" size="small" />
                                                                            )}
                                                                        />
                                                                    )}
                                                                </FormControl>
                                                            );
                                                        })()}
                                                        <Tooltip title="Remove trigger">
                                                            <IconButton
                                                                color="secondary"
                                                                onClick={() => onRemoveTrigger(clientId, triggerIndex)}
                                                                aria-label="Remove trigger"
                                                            >
                                                                <DeleteOutline />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        )}
                                    </Draggable>
                                ))}
                                {triggerDroppableProvided.placeholder}
                            </Stack>
                        )}
                    </StrictModeDroppable>
                )}
            </Stack>
        );
    };

    return (
        <Stack spacing={2} sx={{ width: "100%" }}>
            <DragDropContext onDragEnd={(result: DropResult) => {
                    if (result.type === 'TRIGGER') {
                        if (result.destination && onTriggerDragEnd) {
                            const questionClientId = result.source.droppableId.replace('triggers-', '');
                            onTriggerDragEnd(questionClientId, result.source.index, result.destination.index);
                        }
                    } else {
                        onDragEnd(result);
                    }
                }}>
                <StrictModeDroppable droppableId={droppableId} type="QUESTION">
                    {(droppableProvided) => (
                        // deepcode ignore DomXss: False positive - react-beautiful-dnd droppableProps are safe library props
                        <Stack spacing={2} ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                            {questions.map((workflowQuestion, index) => {
                                
                                
                                const question = workflowQuestion?.question;
                                if (!question) {
                                    return null;
                                }
                                const questionKey =
                                    workflowQuestion.clientId ?? String(question.id ?? index);
                                const isCollapsed = collapsedSections[questionKey] ?? false;
                                const draggableId = workflowQuestion.clientId ?? `workflow-question-${questionKey}`;

                                return (
                                    <Draggable key={draggableId} draggableId={draggableId} index={index}>
                                        {(draggableProvided, snapshot) => (
                                            <Paper
                                                ref={draggableProvided.innerRef}
                                                {...draggableProvided.draggableProps}
                                                sx={{
                                                    width: "100%",
                                                    backgroundColor: questionCardPalette.background,
                                                    borderColor: questionCardPalette.border,
                                                    borderWidth: 1,
                                                    borderStyle: "solid",
                                                    flexDirection: "column",
                                                    opacity: snapshot.isDragging ? 0.98 : 1,
                                                    transition: "opacity 0.2s ease",
                                                    boxShadow: "none",
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ py: 1.5, px: 2 }}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                        <Box
                                                            component="span"
                                                            {...draggableProvided.dragHandleProps}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                cursor: "grab",
                                                                color: "text.secondary",
                                                            }}
                                                        >
                                                            <DragIndicator fontSize="small" />
                                                        </Box>
                                                        <Typography variant="subtitle1">
                                                            {question.name}
                                                        </Typography>
                                                        <Chip
                                                            label={question.questionType}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    </Stack>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip title={isCollapsed ? "Expand section" : "Collapse section"}>
                                                            <IconButton onClick={() => onToggleSection(questionKey)}>
                                                                {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Remove question">
                                                            <IconButton onClick={() => onRemoveQuestion(workflowQuestion.clientId)}>
                                                                <DeleteOutline />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Stack>
                                                <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                                                    <Divider sx={{ mx: 2 }} />
                                                    <Box sx={{ backgroundColor: questionCardPalette.background, p: 2 }}>
                                                        {question.questionText ? (
                                                            <Typography variant="body2" color="text.secondary" mb={2}>
                                                                {question.questionText}
                                                            </Typography>
                                                        ) : null}
                                                        {question.questionType === 'SYSTEM' && (
                                                            <Box sx={{ mb: 2 }}>
                                                                <ExpressionBuilder
                                                                    expression={workflowQuestion.question.expression ?? ''}
                                                                    onChange={(expr) => onExpressionChange?.(workflowQuestion.clientId, expr)}
                                                                    disabled
                                                                />
                                                            </Box>
                                                        )}
                                                        {renderTriggerSection(workflowQuestion)}
                                                    </Box>
                                                </Collapse>
                                            </Paper>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {droppableProvided.placeholder as any}
                        </Stack>
                    )}
                </StrictModeDroppable>
            </DragDropContext>

            {showAddQuestion && questionsAvailableToAdd.length > 0 && (
                <>
                    <Divider />
                    <AddQuestionAutocomplete
                        options={questionsAvailableToAdd}
                        onAddQuestion={onAddQuestion}
                    />
                </>
            )}
        </Stack>
    );
};

export default QuestionsSection;
